// @ts-nocheck
import { getApiCall } from "../service/httpCall";
import {
  API_CDR_MEDIA_MODE,
  API_RECORDINGS_AUDIO,
  API_RECORDINGS_DETAILS,
  API_VOICEMAILS_AUDIO,
  API_VOICEMAILS_DETAILS,
} from "../service/url";
import { STORAGE_API_BASE_URL, BACKEND_API_BASE_URL } from "./constant";
import { storageUrlOr } from "./storageUrl";

// Inline-mode `recording_file` (JSON array of files) → entries with a
// playable `url`. Voicemail entries resolve to the voicemails folder.
export const getRecordingData = (recordingFile) => {
  try {
    if (!recordingFile) return [];
    const recordings =
      typeof recordingFile === "string" ? JSON.parse(recordingFile) : recordingFile;
    const baseUrl = BACKEND_API_BASE_URL.replace(/\/api\/?$/, "");
    return recordings.map((rec) => {
      const folder = rec.type?.toLowerCase() === "voicemail" ? "voicemails" : "recordings";
      return { ...rec, url: rec.url || `${baseUrl}/recordings/${folder}/${rec.name}` };
    });
  } catch (err) {
    console.log("Recording parse error:", err);
    return [];
  }
};

/**
 * Recordings / voicemails table rows are playable only at `status` 1.
 * Anything else (still processing, failed, missing) keeps Play disabled.
 */
export const isMediaReady = (row) => Number(row?.status) === 1;
export const MEDIA_NOT_READY_TITLE = "Audio not available yet";

export const CDR_MEDIA_MODES = {
  INLINE: "inline",
  REFERENCE: "reference",
};

const REFERENCE_MARKER = "reference";

let cachedMode = null;
let inFlightModeRequest = null;

export const getCdrMediaMode = async () => {
  if (cachedMode) return cachedMode;
  if (inFlightModeRequest) return inFlightModeRequest;

  inFlightModeRequest = getApiCall(API_CDR_MEDIA_MODE)
    .then((resp) => {
      const mode =
        resp?.cdr_media_mode === CDR_MEDIA_MODES.REFERENCE
          ? CDR_MEDIA_MODES.REFERENCE
          : CDR_MEDIA_MODES.INLINE;
      cachedMode = mode;
      return mode;
    })
    .catch(() => CDR_MEDIA_MODES.INLINE)
    .finally(() => {
      inFlightModeRequest = null;
    });

  return inFlightModeRequest;
};

export const parseCdrMediaReference = (recordingFile) => {
  if (!recordingFile) return null;

  let parsed = recordingFile;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  if (parsed.mode !== REFERENCE_MARKER) return null;

  const toIds = (value) =>
    (Array.isArray(value) ? value : [value])
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n) && n > 0);

  const recordingIds = toIds(parsed.recordings);
  const voicemailIds = toIds(parsed.voicemails);

  return { recordingIds, voicemailIds };
};

export const hasCdrMediaReference = (recordingFile) => {
  const ref = parseCdrMediaReference(recordingFile);
  if (!ref) return false;
  return ref.recordingIds.length > 0 || ref.voicemailIds.length > 0;
};

const buildMediaAudioUrl = (template, id) => {
  const path = template.replace(":id", String(id));
  const base = String(STORAGE_API_BASE_URL || "").replace(/\/+$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

export const buildRecordingAudioUrl = (id) =>
  buildMediaAudioUrl(API_RECORDINGS_AUDIO, id);

export const buildVoicemailAudioUrl = (id) =>
  buildMediaAudioUrl(API_VOICEMAILS_AUDIO, id);

export const fetchCdrMediaDetails = async (recordingFile) => {
  const ref = parseCdrMediaReference(recordingFile);
  if (!ref) return { recordings: [], voicemails: [], errors: [] };

  const errors = [];

  const load = async (url, ids, label) => {
    if (!ids.length) return [];
    try {
      const resp = await getApiCall(`${url}?ids=${ids.join(",")}`);
      return resp?.rows || [];
    } catch (err) {
      errors.push(`${label}: ${typeof err === "string" ? err : err?.message || "failed"}`);
      return [];
    }
  };

  const [recordings, voicemails] = await Promise.all([
    load(API_RECORDINGS_DETAILS, ref.recordingIds, "Recordings"),
    load(API_VOICEMAILS_DETAILS, ref.voicemailIds, "Voicemails"),
  ]);

  return {
    recordings: recordings.map((row) => ({
      ...row,
      kind: "recording",
      audio_url: storageUrlOr("recordings", row.blob_path, buildRecordingAudioUrl(row.id)),
    })),
    voicemails: voicemails.map((row) => ({
      ...row,
      kind: "voicemail",
      audio_url: storageUrlOr("voicemails", row.blob_path, buildVoicemailAudioUrl(row.id)),
    })),
    errors,
  };
};
